'use client';

import React, { useCallback } from 'react';
import { useFormInputProps, useEditorRef } from '@udecode/plate-common';
import {
  flip,
  offset,
} from '@udecode/plate-floating';
import {
  FloatingLinkUrlInput,
  LinkOpenButton,
  useFloatingLinkEdit,
  useFloatingLinkEditState,
  useFloatingLinkInsert,
  useFloatingLinkInsertState,
  submitFloatingLink,
  floatingLinkActions,
} from '@udecode/plate-link';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const floatingOptions = {
  middleware: [
    offset(12),
    flip({
      fallbackPlacements: ['bottom-end', 'top-start', 'top-end'],
      padding: 12,
    }),
  ],
  placement: 'bottom-start',
};

const protocolRE = /^(?:\w+:)?\/\//;

export function LinkFloatingToolbar({ state }) {
  const editor = useEditorRef();
  const insertState = useFloatingLinkInsertState({
    ...state,
    floatingOptions: {
      ...floatingOptions,
      ...state?.floatingOptions,
    },
  });
  const {
    hidden,
    props: insertProps,
    ref: insertRef,
    textInputProps,
  } = useFloatingLinkInsert(insertState);

  const editState = useFloatingLinkEditState({
    ...state,
    floatingOptions: {
      ...floatingOptions,
      ...state?.floatingOptions,
    },
  });
  const {
    editButtonProps,
    props: editProps,
    ref: editRef,
    unlinkButtonProps,
  } = useFloatingLinkEdit(editState);
  const inputProps = useFormInputProps({
    preventDefaultOnEnterKeydown: true,
  });

  const onChange = useCallback((e) => {
    const { value } = e.target;
    const match = value.match(protocolRE);

    if (!match) {
      if (value.length === 1) {
        e.target.value = `https://${value}`;
      }
      else {
        e.target.value = 'https://';
      }
    }

    // Check if the URL contains more than one protocol and remove duplicates.
    // This causes the sanitizer to delete the html.
    const regex = /(https?:\/\/)/g;
    const matches = e.target.value.match(regex);
    if (matches && matches.length > 1) {
      e.target.value = e.target.value.replace(/(https?:\/\/)+/g, 'https://');
    }

    floatingLinkActions.url(e.target.value);
  }, []);

  if (hidden) return null;

  const input = (
    <div
      className="slate-ui-floating slate-ui-floating--link slate-ui-floating--insert"
      {...inputProps}
    >
      <div className="slate-ui-floating__inner form-group">
        <div className="slate-ui-floating__input">
          <FontAwesomeIcon icon={['fal', 'external-link']}
            size="lg"
          />
          <FloatingLinkUrlInput
            className=""
            placeholder="Paste link"
            onChange={onChange}
          />
        </div>
        <div className="slate-ui-floating__divider" />
        <div className="slate-ui-floating__input">
          <FontAwesomeIcon icon={['fal', 'align-left']}
            size="lg"
          />
          <input
            className=""
            placeholder="Text to display"
            {...textInputProps}
          />
          <button
            className="slate-ui-floating__apply-button button--unstyled"
            onClick={() => submitFloatingLink(editor)}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );

  const editContent = editState.isEditing ? (
    input
  ) : (
    <div className="slate-ui-floating slate-ui-floating--link slate-ui-floating--edit slate-ui-floating--width-auto">
      <div className="slate-ui-floating__inner slate-ui-floating__inner--inline">
        <button
          className="slate-ui-floating__button button--unstyled"
          type="button"
          {...editButtonProps}
        >
          Edit link
        </button>

        <div className="slate-ui-floating__divider" />

        <LinkOpenButton
          className="slate-ui-floating__button button--unstyled"
        >
          <FontAwesomeIcon icon={['fal', 'external-link']}
            size="lg"
          />
        </LinkOpenButton>

        <div className="slate-ui-floating__divider" />

        <button
          className="slate-ui-floating__button button--unstyled"
          type="button"
          {...unlinkButtonProps}
        >
          Unlink
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div
        ref={insertRef}
        {...insertProps}
      >
        {input}
      </div>

      <div
        ref={editRef}
        {...editProps}
      >
        {editContent}
      </div>
    </>
  );
}
